import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import HelpIcon from '@mui/icons-material/Help';
import { useAssistantAiActions } from 'features/assistant-ai/hooks';
import { USER_LOADED_STATUS, useSingleUser } from 'features/users';
import CircularProgress from '@mui/material/CircularProgress';
import { FEATURE_FLAGS } from '@pomp-libs/core';
import { useAuth } from 'features/auth';

const AiGeneratedMessage = ({ onChange, userId, estheticianId }) => {
  const assistantAiActions = useAssistantAiActions();
  const { user } = useSingleUser(userId, false, USER_LOADED_STATUS.BASIC);
  const { user: esthetician } = useSingleUser(
    estheticianId,
    false,
    USER_LOADED_STATUS.BASIC
  );
  const [loading, setLoading] = useState(false);
  const { hasFeaturePermission, user: authUser } = useAuth();

  if (!hasFeaturePermission(FEATURE_FLAGS.AI_GENERATE_MESSAGES)) return null;

  const handlePersonalizedMessageClick = async () => {
    let promptVariables = `
      Client Name: ${user.firstName} ${user.lastName}. 
      Esthetician Name: ${esthetician.firstName} ${esthetician.lastName}. 
      `;

    const promoCode = authUser?.toJS()?.estheticianPromoCode;

    if (promoCode) {
      promptVariables += `Client Promo Code: ${promoCode}.`;
    }

    setLoading(true);

    const message = await assistantAiActions.assistantAiChat({
      promptVariables,
      userId,
      estheticianId,
    });
    const onChangeReply = { target: { value: message } };
    onChange(onChangeReply);
    setLoading(false);
  };

  return (
    <Box sx={{ mb: 1, display: 'inline', mx: 'auto' }}>
      <Button
        variant="outlined"
        startIcon={
          loading ? <CircularProgress size="16px" /> : <AutoFixHighIcon />
        }
        disabled={loading}
        onClick={handlePersonalizedMessageClick}
      >
        Draft an Intro Message
      </Button>
      <Tooltip
        enterTouchDelay={0}
        title={
          <Typography variant="paragraph">
            Use AI to draft your intro message to this client. You will be able
            to edit the message before sending
          </Typography>
        }
      >
        <HelpIcon
          sx={{
            ml: 1,
            verticalAlign: 'text-top',
            mb: 2,
            color: 'text.secondary',
          }}
        />
      </Tooltip>
    </Box>
  );
};

AiGeneratedMessage.propTypes = {
  onChange: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  estheticianId: PropTypes.string.isRequired,
};

export default AiGeneratedMessage;
